/**
 * Helpers shared by the masked trucker/transporter list views that call
 * GET pipe/search.
 *
 * - buildMaskedSearchEndpoint encodes every value, so a raw `&`, `#`, `+` or
 *   `%` in a name / contact person / PAN cannot split or corrupt the query
 *   string (e.g. "L & T LOGISTICS" used to reach the server as name=L).
 * - canSearchMasked mirrors the server rule: name and contact person are only
 *   used when they have at least 3 characters, phone and PAN only when they
 *   are 10 characters long. Searching with nothing usable makes the server
 *   build an empty filter.
 * - normalisePhone turns what the phone field accepts ("+91 98765 43210",
 *   "+919876543210", "09876543210") into the 10-digit number the server
 *   matches exactly against customers.phone_number.
 */
export const MIN_SEARCH_TERM_LENGTH = 3;
export const SHORT_SEARCH_TERM_HINT = 'Enter at least 3 characters';
export const PAN_LENGTH_HINT = 'PAN must be 10 characters';

const clean = value =>
  value === undefined || value === null ? '' : String(value).trim();

// Count code points, not UTF-16 units, to match the server's Python len().
const len = value => [...clean(value)].length;

// Optional country code (+1 to +999) or trunk 0, then exactly 10 digits.
const PHONE_WITH_PREFIX = /^(?:\+\d{1,3}|0)?(\d{10})$/;

export const normalisePhone = value => {
  const compact = clean(value).replace(/[\s-]/g, '');
  const match = compact.match(PHONE_WITH_PREFIX);
  return match ? match[1] : compact;
};

export const isShortSearchTerm = value => {
  const length = len(value);
  return length > 0 && length < MIN_SEARCH_TERM_LENGTH;
};

export const isIncompletePan = value => {
  const length = len(value);
  return length > 0 && length !== 10;
};

export const canSearchMasked = ({ name, contactPerson, phone, pan } = {}) =>
  len(name) >= MIN_SEARCH_TERM_LENGTH ||
  len(contactPerson) >= MIN_SEARCH_TERM_LENGTH ||
  /^\d{10}$/.test(normalisePhone(phone)) ||
  len(pan) === 10;

/**
 * Builds `pipe/search?type=..&limit=..&offset=..&tab_info=..&role=..&<filters>`.
 * Filters are appended in the order given, trimmed, and skipped when empty.
 * phone_number is normalised to its 10-digit form.
 */
export const buildMaskedSearchEndpoint = ({
  type,
  limit = 10,
  offset,
  tabInfo,
  role,
  filters = {},
}) => {
  const params = new URLSearchParams();
  params.append('type', type);
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  params.append('tab_info', tabInfo);
  params.append('role', role);
  Object.keys(filters).forEach(key => {
    const value =
      key === 'phone_number'
        ? normalisePhone(filters[key])
        : clean(filters[key]);
    if (value) params.append(key, value);
  });
  return `pipe/search?${params.toString()}`;
};
